import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import cx from 'classnames';
import { useModal } from 'components/modals';
import {
  TrialEndedModalBody,
  TrialModalBody,
  TrialModalBodySmall
} from 'features/forceTrial/TrialModalBodies';
import { TrialModalState } from 'features/forceTrial/useHasTrialModalBeenClosedSomeTimeAgo';
import { logError } from 'features/logging/logError';
import { useAvailablePlans } from 'features/pricing/hook/useAvailablePlans';
import { PricingPlan } from 'features/pricing/types';
import { ErrorBoundary } from 'features/react-error-boundary/ErrorBoundary';
import { StripeEmbeddedModal } from 'features/stripe-portal/StripeEmbeddedModal';
import useUpdateSubscriptionModal from 'features/updateSubscriptionModal/hook/useUpdateSubscriptionModal';
import { useEffect } from 'react';
import ReactModal from 'react-modal';
import { GAEvents } from 'services/tracking/GAEvents';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { useAppThemeStore } from 'styles/new-theme/useAppThemeStore';

type Props = {
  isSubscriptionSetToFree: boolean;
  trialModalClosedTimer: TrialModalState;
  // Sorry, I had a hard time coming up with better names for these props
  onModalSuccessfulClose: () => void;
  onModalFailClose: () => void;
  onModalTemporaryClose: () => void;
};

export const TrialModal = ({
  isSubscriptionSetToFree,
  trialModalClosedTimer,
  onModalSuccessfulClose,
  onModalFailClose,
  onModalTemporaryClose
}: Props) => {
  const theme = useTheme();
  const { showModal } = useModal();

  const themeMode = useAppThemeStore(state => state.themeMode);
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('lg'));

  const enforceFreeTrial = useAppSelector(state => state.customer.enforceFreeTrial);
  const preferredPricingId = useAppSelector(state => state.customer.preferredPricingId);

  const { data: allPlansData } = useAvailablePlans({});
  const allPlans = allPlansData?.allPlans;
  const preferredPlan = allPlans?.find(plan => plan.id === preferredPricingId);
  const { data } = useAvailablePlans({ period: 'month' });
  const { starterPlan, proPlan } = data ?? {};

  const showUpgradeSubscriptionModal = useUpdateSubscriptionModal();

  useEffect(() => {
    GAEvents.modalSeen({
      modal: 'trial modal'
    });
  }, []);

  const handlePrimaryAction = () => {
    if (!proPlan) {
      logError('proPlan is undefined', 'react', {
        proPlan: proPlan
      });
      return;
    }

    GAEvents.beginCheckout({
      plan: PricingPlan.pro,
      period: 'month'
    });

    showModal('STRIPE_EMBEDDED_PAYMENT', {
      size: 1100,
      pricingId: proPlan.id
    });
  };

  const handleSecondaryAction = () => {
    if (enforceFreeTrial) {
      if (!starterPlan) {
        logError('starterPlan is undefined', 'react', {
          starterPlan: starterPlan
        });
        return;
      }

      showModal('STRIPE_EMBEDDED_PAYMENT', {
        size: 1100,
        pricingId: starterPlan.id
      });

      return;
    }

    if (enforceFreeTrial === false || enforceFreeTrial === 0) {
      onModalSuccessfulClose();
      return;
    }

    logError('enforceFreeTrial is neither true nor false', 'react', {
      enforceFreeTrial: enforceFreeTrial
    });
  };

  let modalBody;
  // Redirect directly to stripe if the customer has a specific pricingId
  if (preferredPricingId && trialModalClosedTimer === 'never_shown') {
    modalBody = (
      <StripeEmbeddedModal
        pricingId={preferredPricingId}
        preferredPlan={preferredPlan}
        closeModal={() => undefined}
      />
    );
  } else if (isSubscriptionSetToFree) {
    modalBody = (
      <TrialEndedModalBody
        primaryAction={showUpgradeSubscriptionModal}
        secondaryAction={onModalFailClose}
      />
    );
  } else if (isSmallDevice) {
    modalBody = (
      <TrialModalBodySmall
        enforceFreeTrial={enforceFreeTrial}
        primaryAction={handlePrimaryAction}
        secondaryAction={handleSecondaryAction}
      />
    );
  } else {
    modalBody = (
      <TrialModalBody
        enforceFreeTrial={enforceFreeTrial}
        primaryAction={handlePrimaryAction}
        secondaryAction={handleSecondaryAction}
      />
    );
  }

  return (
    <ReactModal
      isOpen={true}
      bodyOpenClassName="modal-open"
      overlayClassName={cx('modal fade', 'show')}
      className={cx('modal-dialog', isSmallDevice ? '' : 'modal-1100')}
      shouldCloseOnOverlayClick={!enforceFreeTrial}
      shouldCloseOnEsc={!enforceFreeTrial}
      shouldFocusAfterRender={true}
      onRequestClose={onModalTemporaryClose}
    >
      <ErrorBoundary>
        <div className={cx('modal-content', themeMode)}>
          <ModalWrapper>{modalBody}</ModalWrapper>
        </div>
      </ErrorBoundary>
    </ReactModal>
  );
};

const ModalWrapper = styled.div`
  z-index: 1;
`;
